// Carrega o que a rede tem (postos, combustíveis, Padrão em vigor, etiquetas...) e monta o `Ctx` do núcleo.
// Tudo filtrado pela rede escolhida (chainId). Resultado em cache do react-query: voltar ao cadastro não repete.
import { useCallback, useMemo } from 'react'
import { useDataProvider, useGetOne } from 'react-admin'
import { useQuery } from '@tanstack/react-query'
import { TagType } from '../../../../models/tagType'
import { DataState } from '../types'
import type { ChainInfo, CityStats, Counts, Ctx, DefaultInfo, FuelInfo, NamedItem, RecentSpecial, StationInfo } from '../types'
import { toCityStats, toDefaultInfo, toList, toRecent } from './mappers'

export { toDefaultInfo, toCityStats } from './mappers'

const STALE = 5 * 60 * 1000
const NO_COUNTS: Counts = {}
const ALL = { page: 1, perPage: 9999 }
/** A URL leva a lista de postos no filtro; em pedaços para redes grandes (a maior tem 88 postos). */
const CHUNK = 50

/** Uma lista que vem da rede: como está e como pedir de novo. */
export interface ListStatus {
  state: DataState
  retry: () => void
}

/** `data` presente = pronto (uma busca de fundo que falha não derruba o que já chegou). */
const stateOf = (q: { data: unknown; isError: boolean }): DataState =>
  q.data !== undefined ? DataState.ready : q.isError ? DataState.error : DataState.loading

export interface DiscountCtxState {
  /** null enquanto a rede, os postos ou os combustíveis não chegaram. */
  ctx: Ctx | null
  loading: boolean
  /** Falhou carregar algo essencial (rede, postos ou combustíveis). */
  failed: boolean
  retry: () => void
  /** Busca de novo o Padrão em vigor (depois de uma falha ao salvar). */
  refetchDefaults: () => Promise<DefaultInfo[]>
  /** Padrão em vigor, etiquetas, grupos e cidades: cada um mostra o próprio erro, com "Tentar de novo". */
  lists: { defaults: ListStatus; tags: ListStatus; groups: ListStatus; cities: ListStatus }
  /** Contas de cidade (GET customer-city-stats): `pending` enquanto não chegou nem falhou. */
  cityStats: { data: CityStats | null; pending: boolean }
}

export function useDiscountCtx(chainId: string, now: Date, extra: { loadRecent: boolean; loadCities: boolean }): DiscountCtxState {
  const dataProvider = useDataProvider()
  const enabled = !!chainId
  const { data: chainRecord, isError: chainError, refetch: refetchChain } = useGetOne('chains', { id: chainId }, { enabled })

  const stationsQ = useQuery({
    queryKey: ['discountV2', chainId, 'stations'],
    enabled,
    staleTime: STALE,
    queryFn: async (): Promise<StationInfo[]> => {
      const res = await dataProvider.getList('stations', { pagination: ALL, sort: { field: 'name', order: 'ASC' }, filter: {} })
      return toList(res).map(s => ({ id: s.id, name: s.name, city: s.city }))
    },
  })
  const stations = stationsQ.data
  const stationKey = useMemo(() => (stations ? stations.map(s => s.id).sort().join(',') : ''), [stations])

  const fuelsQ = useQuery({
    queryKey: ['discountV2', chainId, 'fuels', stationKey],
    enabled: enabled && !!stations && stations.length > 0,
    staleTime: STALE,
    queryFn: async (): Promise<FuelInfo[]> => {
      const ids = (stations as StationInfo[]).map(s => s.id)
      const parts: string[][] = []
      for (let i = 0; i < ids.length; i += CHUNK) parts.push(ids.slice(i, i + CHUNK))
      const results = await Promise.all(parts.map(placeIds =>
        dataProvider.getList('fuels', { pagination: ALL, sort: { field: 'name', order: 'ASC' }, filter: { placeIds } })))
      const byId = new Map<string, FuelInfo>()
      results.forEach(res => toList(res).forEach(f => {
        const have = byId.get(f.id) || { id: f.id, name: f.name, stationIds: [] as string[] }
        ;(f.placeFuels || []).forEach((pf: any) => { if (pf.placeId && !have.stationIds.includes(pf.placeId)) have.stationIds.push(pf.placeId) })
        byId.set(f.id, have)
      }))
      return Array.from(byId.values())
    },
  })

  const defaultsQ = useQuery({
    queryKey: ['discountV2', chainId, 'defaults'],
    retry: 1,
    enabled,
    staleTime: 0,
    queryFn: async (): Promise<DefaultInfo[]> => {
      const res = await dataProvider.getList('discounts', { pagination: ALL, sort: { field: 'createdAt', order: 'DESC' }, filter: { chainId, isDefault: true, isEnabled: true } })
      return toList(res).map(toDefaultInfo)
    },
  })

  const tagsQ = useQuery({
    queryKey: ['discountV2', chainId, 'tags'],
    retry: 1,
    enabled,
    staleTime: STALE,
    queryFn: async (): Promise<NamedItem[]> => {
      const res = await dataProvider.getList('chains/chainId/tag', { pagination: ALL, sort: { field: 'name', order: 'ASC' }, filter: { type: TagType.client, chainId } })
      return toList(res).map(t => ({ id: t.id, name: t.name }))
    },
  })
  const groupsQ = useQuery({
    queryKey: ['discountV2', chainId, 'groups'],
    retry: 1,
    enabled,
    staleTime: STALE,
    queryFn: async (): Promise<NamedItem[]> => {
      const res = await dataProvider.getList('chains/chainId/customer-groups', { pagination: ALL, sort: { field: 'name', order: 'ASC' }, filter: {} })
      return toList(res).map(g => ({ id: g.id, name: g.name }))
    },
  })
  const citiesQ = useQuery({
    queryKey: ['discountV2', chainId, 'cities'],
    retry: 1,
    enabled: enabled && extra.loadCities,
    staleTime: STALE,
    queryFn: async (): Promise<NamedItem[]> => {
      const res = await dataProvider.getList('cities', { pagination: { page: 1, perPage: 2000 }, sort: { field: 'name', order: 'ASC' }, filter: { chainId } })
      return toList(res).map(c => ({ id: c.id, name: c.name }))
    },
  })
  const cityStatsQ = useQuery({
    queryKey: ['discountV2', chainId, 'cityStats'],
    enabled: enabled && extra.loadCities,
    staleTime: STALE,
    retry: false,
    queryFn: async (): Promise<CityStats> => toCityStats(await dataProvider.getOne('chains/chainId/customer-city-stats', { id: '' })),
  })
  const recentQ = useQuery({
    queryKey: ['discountV2', chainId, 'recent'],
    enabled: enabled && extra.loadRecent,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<RecentSpecial[]> => {
      const res = await dataProvider.getList('discounts', { pagination: { page: 1, perPage: 15 }, sort: { field: 'createdAt', order: 'DESC' }, filter: { chainId, type: 'special', isEnabled: true } })
      return toList(res).map(toRecent)
    },
  })

  const chain: ChainInfo | null = useMemo(() => (chainRecord ? {
    name: chainRecord.name,
    appColor: chainRecord.appColor,
    useGift: !!chainRecord.useGift,
    useCustomerRecovery: !!chainRecord.useCustomerRecovery,
    useDotz: !!chainRecord.useDotz,
    isPriceIncreaseEnabled: !!chainRecord.isPriceIncreaseEnabled,
    pointsWhenDiscounting: typeof chainRecord.pointsWhenDiscounting === 'boolean' ? chainRecord.pointsWhenDiscounting : undefined,
  } : null), [chainRecord])

  const ready = !!chain && !!stationsQ.data && (stationsQ.data.length === 0 || !!fuelsQ.data)
  const failed = chainError || stationsQ.isError || fuelsQ.isError
  const defaultsState = stateOf(defaultsQ)

  const ctx = useMemo<Ctx | null>(() => {
    if (!ready || !chain) return null
    return {
      now, chainId, chain,
      stations: stationsQ.data as StationInfo[],
      fuels: fuelsQ.data ?? [],
      defaults: defaultsQ.data ?? [],
      // Enquanto o Padrão em vigor não chegou (ou falhou), o cadastro não afirma que "não há Padrão".
      defaultsState: defaultsState,
      tags: tagsQ.data ?? [],
      groups: groupsQ.data ?? [],
      cities: citiesQ.data ?? [],
      recent: recentQ.data ?? [],
      // as contagens de clientes vêm da tela (dependem do que o dono escolheu) e entram por cima
      counts: NO_COUNTS,
      cityStats: cityStatsQ.data ?? null,
    }
  }, [ready, chain, now, chainId, stationsQ.data, fuelsQ.data, defaultsQ.data, defaultsState, tagsQ.data, groupsQ.data, citiesQ.data, recentQ.data, cityStatsQ.data])

  const retry = useCallback(() => {
    refetchChain()
    stationsQ.refetch()
    fuelsQ.refetch()
    defaultsQ.refetch()
  }, [refetchChain, stationsQ, fuelsQ, defaultsQ])

  const refetchDefaults = useCallback(async () => (await defaultsQ.refetch()).data ?? [], [defaultsQ])

  const lists = useMemo(() => ({
    defaults: { state: defaultsState, retry: () => { void defaultsQ.refetch() } },
    tags: { state: stateOf(tagsQ), retry: () => { void tagsQ.refetch() } },
    groups: { state: stateOf(groupsQ), retry: () => { void groupsQ.refetch() } },
    cities: { state: stateOf(citiesQ), retry: () => { void citiesQ.refetch() } },
  }), [defaultsState, defaultsQ, tagsQ, groupsQ, citiesQ])
  const cityStats = useMemo(() => ({ data: cityStatsQ.data ?? null, pending: cityStatsQ.isPending && cityStatsQ.fetchStatus !== 'idle' }), [cityStatsQ.data, cityStatsQ.isPending, cityStatsQ.fetchStatus])

  return { ctx, loading: !ready && !failed, failed, retry, refetchDefaults, lists, cityStats }
}
